import React, { useMemo, useState } from 'react';
import { string, object } from 'prop-types';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import classNames from 'classnames';
import { twitterPageURL } from '../../util/urlHelpers';
import {
  IconSocialMediaFacebook,
  IconSocialMediaInstagram,
  IconSocialMediaTwitter,
  Logo,
  ExternalLink,
  NamedLink,
  LanguageSelect,
} from '../../components';
import IconSocialMediaLinkedIn from '../IconSocialMediaLinkedIn/IconSocialMediaLinkedIn';
import ModalContainer from '../ModalContainer/ModalContainer';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import defaultLocations from '../../config/configDefaultLocationSearches';
import IconSwistainableLogo from '../SectionPartners/logos/IconSwistainableLogo';
import { youtubeIcon } from '../../containers/PageBuilder/Primitives/Link/Icons';
import { useSelector } from 'react-redux';
import IconLogo from '../Logo/IconLogo';
import InstallPrompt from '../InstallPrompt/InstallPrompt';

import css from './Footer.module.css';

const MODAL_TYPE = {
  CHANGE_LANGUAGE: 'changeLanguage',
};

const renderSocialMediaLinks = (intl, config) => {
  const {
    siteFacebookPage,
    siteInstagramPage,
    siteTwitterHandle,
    siteLinkedinPage,
    siteYouTubePage,
  } = config;
  const siteTwitterPage = twitterPageURL(siteTwitterHandle);

  const twitterLink = siteTwitterPage ? (
    <li key="linkToTwitter" className={css.icon}>
      <ExternalLink
        href={siteTwitterPage}
        className={css.icon}
        title={intl.formatMessage({ id: 'Footer.goToTwitter' })}
      >
        <IconSocialMediaTwitter />
      </ExternalLink>
    </li>
  ) : null;

  const facebookLink = siteFacebookPage ? (
    <li key="linkToFacebook" className={css.icon}>
      <ExternalLink
        href={siteFacebookPage}
        className={css.icon}
        title={intl.formatMessage({ id: 'Footer.goToFacebook' })}
      >
        <IconSocialMediaFacebook />
      </ExternalLink>
    </li>
  ) : null;

  const instragramLink = siteInstagramPage ? (
    <li key="linkToInstagram" className={css.icon}>
      <ExternalLink
        href={siteInstagramPage}
        className={css.icon}
        title={intl.formatMessage({ id: 'Footer.goToInstagram' })}
      >
        <IconSocialMediaInstagram />
      </ExternalLink>
    </li>
  ) : null;

  const linkedinLink = siteLinkedinPage ? (
    <li key="linkToLinkedin" className={css.icon}>
      <ExternalLink
        href={siteLinkedinPage}
        className={css.icon}
        title={intl.formatMessage({ id: 'Footer.goToLinkedin' })}
      >
        <IconSocialMediaLinkedIn />
      </ExternalLink>
    </li>
  ) : null;

  const youTubeLink = siteYouTubePage ? (
    <li key="linkToYouTube" className={css.icon}>
      <ExternalLink
        href={siteYouTubePage}
        className={css.icon}
        title={intl.formatMessage({ id: 'Footer.goToYouTube' })}
      >
        {youtubeIcon()}
      </ExternalLink>
    </li>
  ) : null;

  return [twitterLink, facebookLink, instragramLink, linkedinLink, youTubeLink].filter(Boolean);
};

const Footer = props => {
  const { rootClassName, className, location, intl, listing, seoUrlsInAllLang, welfareBranding } = props;

  const config = useConfiguration();

  const { interfaceLang } = useSelector(state => state.ui);

  const [modalTypeOpen, setModalTypeOpen] = useState(null);

  const { partner } = useSelector(state => state.ui);

  const socialMediaLinks = useMemo(() => renderSocialMediaLinks(intl, config), [intl, config]);

  // Map language to localized outdoor stories route name
  const getOutdoorStoriesRouteName = (language) => {
    const langCode = language?.split('-')?.[0] || 'en';
    const routeMap = {
      'en': 'SeoPageOsEn',
      'de': 'SeoPageOsDe',
      'fr': 'SeoPageOsFr',
      'it': 'SeoPageOsIt'
    };
    return routeMap[langCode] || 'SeoPageOsEn'; // fallback to English
  };

  const outdoorStoriesRouteName = getOutdoorStoriesRouteName(interfaceLang);

  const classes = useMemo(() => classNames(rootClassName || css.root, className), [
    className,
    rootClassName,
  ]);

  const footerStyle = welfareBranding?.brandColor
    ? { backgroundColor: welfareBranding.brandColor }
    : {};

  return (
    <>
      <div className={css.installAppWrapper}>
        <InstallPrompt isInline className={css.installApp} />
      </div>
      <footer className={classes} style={footerStyle} aria-label="Site footer">
        <div className={css.topBorderWrapper}>
          <div className={css.content}>
            {/* Brand and About Section */}
            <div className={css.brandSection}>
              <div className={css.brandInfo}>
                <NamedLink name="LandingPage" className={css.logoLink}>
                  <Logo format="mobile" className={css.logo} alt={config.marketplaceName} />
                </NamedLink>
                <div className={css.brandText}>
                  <p className={css.organizationDescription}>
                    <FormattedMessage id="Footer.organizationDescription" />
                  </p>
                  <p className={css.organizationCopyright}>
                    <NamedLink name="LandingPage" className={css.copyrightLink}>
                      <FormattedMessage
                        id="Footer.copyright"
                        values={{ marketplaceName: config.marketplaceName }}
                      />
                    </NamedLink>
                  </p>
                </div>
              </div>

              {/* Social and Badge - Desktop only */}
              <div className={css.socialSection}>
                <div className={css.socialLinks} aria-label="Social media links">
                  <ul className={css.socialList}>{socialMediaLinks}</ul>
                </div>
                <ExternalLink
                  href={partner ? 'https://crion.org' : 'https://www.stnet.ch/de/swisstainable/'}
                  className={classNames(css.partnerBadge, { [css.crionAsPartner]: partner })}
                  title={partner ? 'Powered by Crion' : 'Swisstainable'}
                >
                  {partner ? (
                    <IconLogo format="mobile" className={css.badgeLogo} />
                  ) : (
                    <IconSwistainableLogo />
                  )}
                </ExternalLink>
              </div>
            </div>

            {/* Main Navigation Columns */}
            <nav className={css.navSection} aria-label="Footer navigation">
              <div className={css.navColumn}>
                <h3 className={css.navHeading}>
                  <FormattedMessage id="Footer.discover" defaultMessage="Discover" />
                </h3>
                <ul className={css.list}>
                  <li className={css.listItem}>
                    <NamedLink name="NewListingPage" className={css.link}>
                      <FormattedMessage id="General.addActivity" />
                    </NamedLink>
                  </li>
                  <li className={css.listItem}>
                    <NamedLink
                      name="SearchPage"
                      to={{ search: defaultLocations[0].search }}
                      className={css.link}
                    >
                      <FormattedMessage id="Footer.toBrowseAnActivityPage" />
                    </NamedLink>
                  </li>
                  <li className={css.listItem}>
                    <NamedLink name={outdoorStoriesRouteName} className={css.link}>
                      <FormattedMessage id="Footer.blogs" />
                    </NamedLink>
                  </li>
                  <li className={css.listItem}>
                    <NamedLink name="GiftCardPage" className={css.link}>
                      <FormattedMessage id="Footer.giftCard" defaultMessage="Gift Cards" />
                    </NamedLink>
                  </li>
                  <li className={css.listItem}>
                    <NamedLink name="WelfareIndexPage" className={css.link}>
                      <FormattedMessage id="Footer.welfare" defaultMessage="Corporate Welfare" />
                    </NamedLink>
                  </li>
                </ul>
              </div>

              <div className={css.navColumn}>
                <h3 className={css.navHeading}>
                  <FormattedMessage id="Footer.support" defaultMessage="Support" />
                </h3>
                <ul className={css.list}>
                  <li className={css.listItem}>
                    <NamedLink name="HelpCenterBasePage" className={css.link}>
                      <FormattedMessage id="General.help" />
                    </NamedLink>
                  </li>
                  <li className={css.listItem}>
                    <NamedLink name="ContactPage" className={css.link}>
                      <FormattedMessage id="Footer.toContactPage" />
                    </NamedLink>
                  </li>
                  <li className={css.listItem}>
                    <button
                      className={css.buttonAsLink}
                      onClick={() => setModalTypeOpen(MODAL_TYPE.CHANGE_LANGUAGE)}
                    >
                      <FormattedMessage id="Footer.changeLanguage" />
                    </button>
                  </li>
                </ul>
              </div>

              <div className={css.navColumn}>
                <h3 className={css.navHeading}>
                  <FormattedMessage id="Footer.company" defaultMessage="Company" />
                </h3>
                <ul className={css.list}>
                  <li className={css.listItem}>
                    <NamedLink name="TermsAndConditionsPage" className={css.link}>
                      <FormattedMessage id="Footer.termsOfUse" />
                    </NamedLink>
                  </li>
                  <li className={css.listItem}>
                    <NamedLink name="PrivacyPolicyPage" className={css.link}>
                      <FormattedMessage id="Footer.privacyPolicy" />
                    </NamedLink>
                  </li>
                  <li className={css.listItem}>
                    <NamedLink name="CrionAIPage" className={css.link}>
                      <FormattedMessage id="Footer.crionAI" defaultMessage="Crion AI" />
                    </NamedLink>
                  </li>
                </ul>
              </div>
            </nav>

            {/* Social and Badge Section - Mobile only */}
            <div className={css.socialSectionMobile}>
              <div className={css.socialLinks} aria-label="Social media links">
                <ul className={css.socialList}>{socialMediaLinks}</ul>
              </div>
              <ExternalLink
                href={partner ? 'https://crion.org' : 'https://www.stnet.ch/de/swisstainable/'}
                className={classNames(css.partnerBadge, { [css.crionAsPartner]: partner })}
                title={partner ? 'Powered by Crion' : 'Swisstainable'}
              >
                {partner ? (
                  <IconLogo format="mobile" className={css.badgeLogo} />
                ) : (
                  <IconSwistainableLogo />
                )}
              </ExternalLink>
            </div>
          </div>
        </div>
        <ModalContainer
          id="ChangeLanguageCurrencyModal"
          title={!!modalTypeOpen && intl.formatMessage({ id: `Footer.${modalTypeOpen}` })}
          isModalOpen={!!modalTypeOpen}
          onClose={() => setModalTypeOpen(null)}
        >
          {modalTypeOpen === MODAL_TYPE.CHANGE_LANGUAGE && (
            <LanguageSelect
              location={location}
              embedded
              listing={listing}
              seoUrlsInAllLang={seoUrlsInAllLang}
            />
          )}
        </ModalContainer>
      </footer>
    </>
  );
};

Footer.defaultProps = {
  rootClassName: null,
  className: null,
};

Footer.propTypes = {
  rootClassName: string,
  className: string,
  intl: intlShape.isRequired,
  seoUrlsInAllLang: object,
};

export default compose(
  withRouter,
  injectIntl
)(Footer);
